import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api";
import type { Note } from "./types";

// ---------------------------------------------------------------------------
// Note async thunks
// ---------------------------------------------------------------------------

export const fetchTeamNotes = createAsyncThunk(
    "teamFocusSession/fetchNotes",
    async (
        {
            teamId,
            params,
        }: {
            teamId: string;
            params?: { limit?: number; offset?: number; is_public?: boolean };
        },
        { rejectWithValue },
    ) => {
        try {
            const resp = await api.teamMode.listNotes(teamId, params);
            let data: Note[] = [];
            if (Array.isArray(resp)) {
                data = resp;
            } else if (resp && typeof resp === "object" && "notes" in resp) {
                data = (resp as any).notes || [];
            } else if (resp && typeof resp === "object" && "data" in resp) {
                data = (resp as any).data || [];
            }
            return { teamId, data };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch notes",
            );
        }
    },
);

export const createTeamNote = createAsyncThunk(
    "teamFocusSession/createNote",
    async (
        {
            teamId,
            note,
            is_public,
        }: { teamId: string; note: string; is_public?: boolean },
        { rejectWithValue },
    ) => {
        try {
            const created = await api.teamMode.createNote(teamId, {
                note,
                is_public,
            });
            return {
                teamId,
                note: {
                    id: created?.id ?? `note-${Date.now()}`,
                    note: created?.note ?? note,
                    createdAt: created?.createdAt ?? new Date().toISOString(),
                    is_public: created?.is_public ?? (is_public ?? false),
                    created_by: created?.created_by ?? "",
                } as Note,
            };
        } catch (error: any) {
            console.error("createTeamNote failed", error);
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to create note",
            );
        }
    },
);

export const updateTeamNote = createAsyncThunk(
    "teamFocusSession/updateNote",
    async (
        {
            teamId,
            noteId,
            updates,
        }: {
            teamId: string;
            noteId: string;
            updates: Partial<{ note: string; is_public?: boolean }>;
        },
        { rejectWithValue },
    ) => {
        try {
            const updated = await api.teamMode.updateNote(teamId, noteId, updates);
            return { teamId, noteId, updates: updated };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to update note",
            );
        }
    },
);

export const deleteTeamNote = createAsyncThunk(
    "teamFocusSession/deleteNote",
    async (
        { teamId, noteId }: { teamId: string; noteId: string },
        { rejectWithValue },
    ) => {
        try {
            await api.teamMode.deleteNote(teamId, noteId);
            return { teamId, noteId };
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to delete note",
            );
        }
    },
);
